import { useState } from "react";
import { Link } from "react-router-dom";
import { SERVICE_TYPE_LABELS, formatINR } from "@garage/shared";
import {
  attachmentUrl,
  useDeleteService,
  useService,
  type ServiceRow,
} from "../api/services";
import { Button, Card, Spinner } from "./ui";
import { formatDate, formatKm } from "../lib/format";

const TYPE_TONE: Record<string, string> = {
  PERIODIC: "border-signal/40 text-signal",
  TYRE: "border-petrol/40 text-petrol",
  COMPLAINT: "border-danger/40 text-danger",
};

export function ServiceList({
  rows,
  vehicleId,
}: {
  rows: ServiceRow[];
  vehicleId: string;
}) {
  if (rows.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-ink-2">Nothing logged yet for this vehicle.</p>
        <Link to={`/vehicles/${vehicleId}/services/new`} className="mt-4 inline-block">
          <Button>Log the first service</Button>
        </Link>
      </Card>
    );
  }

  return (
    <ul className="flex flex-col gap-2">
      {rows.map((row) => (
        <ServiceRowItem key={row.id} row={row} vehicleId={vehicleId} />
      ))}
    </ul>
  );
}

function ServiceRowItem({
  row,
  vehicleId,
}: {
  row: ServiceRow;
  vehicleId: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <li>
      <Card className="overflow-hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center gap-3 p-4 text-left hover:bg-line/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-signal"
        >
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{row.title}</span>
              <span
                className={`rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase ${TYPE_TONE[row.type] ?? "border-line text-ink-3"}`}
              >
                {SERVICE_TYPE_LABELS[row.type]}
              </span>
            </div>
            <p className="mt-1 font-mono text-xs text-ink-3">
              {formatDate(row.serviceDate)}
              {row.odometerKm != null && ` · ${formatKm(row.odometerKm)}`}
              {row.centreName && ` · ${row.centreName}`}
              {row.itemCount > 0 && ` · ${row.itemCount} item${row.itemCount > 1 ? "s" : ""}`}
              {row.attachmentCount > 0 && " · 📎"}
            </p>
          </div>
          <span className="font-mono tabular-nums">{formatINR(row.totalCost)}</span>
          <span aria-hidden className="text-ink-3">{open ? "▾" : "▸"}</span>
        </button>

        {open && <ServiceDetail id={row.id} vehicleId={vehicleId} />}
      </Card>
    </li>
  );
}

function ServiceDetail({ id, vehicleId }: { id: string; vehicleId: string }) {
  const { data, isPending } = useService(id);
  const remove = useDeleteService();
  const [busyFile, setBusyFile] = useState<string | null>(null);

  if (isPending) {
    return (
      <div className="border-t border-line p-4">
        <Spinner label="Loading detail" />
      </div>
    );
  }
  if (!data) return null;
  const s = data.service;

  async function openFile(attachmentId: string) {
    setBusyFile(attachmentId);
    try {
      window.open(await attachmentUrl(attachmentId), "_blank", "noopener");
    } finally {
      setBusyFile(null);
    }
  }

  return (
    <div className="flex flex-col gap-4 border-t border-line p-4">
      {s.items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left font-mono text-[10px] tracking-[0.1em] text-ink-3 uppercase">
                <th className="pb-1 pr-3">Item</th>
                <th className="pb-1 pr-3">Qty</th>
                <th className="pb-1 pr-3">Unit</th>
                <th className="pb-1 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {s.items.map((i, n) => (
                <tr key={i.id ?? n} className="border-t border-line/60">
                  <td className="py-1.5 pr-3">
                    {i.name}
                    {i.warrantyMonths ? (
                      <span className="ml-2 font-mono text-[10px] text-ink-3">
                        {i.warrantyMonths}mo warranty
                      </span>
                    ) : null}
                  </td>
                  <td className="py-1.5 pr-3 tabular-nums">{i.quantity}</td>
                  <td className="py-1.5 pr-3 tabular-nums">
                    {i.unitCost ? formatINR(i.unitCost) : "—"}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {i.amount ? formatINR(i.amount) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
        <Bit label="Parts" value={s.partsCost ? formatINR(s.partsCost) : "—"} />
        <Bit label="Labour" value={s.labourCost ? formatINR(s.labourCost) : "—"} />
        <Bit label="Days" value={String(s.durationDays)} />
        <Bit label="Next due" value={s.nextDueDate ? formatDate(s.nextDueDate) : "—"} />
      </dl>

      {s.notes && <p className="text-sm text-ink-2">{s.notes}</p>}

      {s.attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {s.attachments.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => openFile(a.id)}
              disabled={busyFile === a.id}
              className="rounded border border-line px-2.5 py-1 font-mono text-xs text-ink-2 hover:border-signal hover:text-signal disabled:opacity-50"
            >
              {busyFile === a.id ? "Opening…" : `📎 ${a.filename}`}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2 border-t border-line pt-3">
        <Link to={`/vehicles/${vehicleId}/services/${s.id}/edit`}>
          <Button variant="ghost" className="!py-1.5 text-sm">Edit</Button>
        </Link>
        <Button
          variant="ghost"
          className="!py-1.5 text-sm"
          disabled={remove.isPending}
          onClick={() => {
            if (confirm(`Delete "${s.title}"? It can be restored from the database.`)) {
              remove.mutate(s.id);
            }
          }}
        >
          Delete
        </Button>
      </div>
    </div>
  );
}

function Bit({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="font-mono text-[10px] tracking-[0.1em] text-ink-3 uppercase">
        {label}
      </dt>
      <dd className="mt-0.5 tabular-nums">{value}</dd>
    </div>
  );
}
